import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class Scoring extends BaseObj {
    public ID: number;
    public __id: number;

    public CompletedPoints: number | null;
    public __completedpoints: number | null;

    public DNFPoints: number | null;
    public __dnfpoints: number | null;

    public DNQPoints: number | null;
    public __dnqpoints: number | null;

    public DNSPoints: number | null;
    public __dnspoints: number | null;

    public DSQPoints: number | null;
    public __dsqpoints: number | null;

    public LAPPoints: number | null;
    public __lappoints: number | null;

    public Name: string;
    public __name: string;

    public NonRankedPoints: number | null;
    public __nonrankedpoints: number | null;

    public OrganizerID: number | null;
    public __organizerid: number | null;

    public Season: number | null;
    public __season: number | null;

    public ShootingPoints: number | null;
    public __shootingpoints: number | null;

    public SportID: number | null;
    public __sportid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        CompletedPoints?: number | null;
        DNFPoints?: number | null;
        DNQPoints?: number | null;
        DNSPoints?: number | null;
        DSQPoints?: number | null;
        EntityState?: EntityState;
        LAPPoints?: number | null;
        Name?: string | null;
        NonRankedPoints?: number | null;
        OrganizerID?: number | null;
        Season?: number | null;
        ShootingPoints?: number | null;
        SportID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Scoring>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Scoring tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CompletedPoints = values.CompletedPoints ?? null;
        this.__completedpoints = values.CompletedPoints ?? null;
        Object.defineProperty(this, 'CompletedPoints', {
            get(): number | null {
                return this.__completedpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('CompletedPoints', this.__completedpoints, value);
                this.__completedpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNFPoints = values.DNFPoints ?? null;
        this.__dnfpoints = values.DNFPoints ?? null;
        Object.defineProperty(this, 'DNFPoints', {
            get(): number | null {
                return this.__dnfpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DNFPoints', this.__dnfpoints, value);
                this.__dnfpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNQPoints = values.DNQPoints ?? null;
        this.__dnqpoints = values.DNQPoints ?? null;
        Object.defineProperty(this, 'DNQPoints', {
            get(): number | null {
                return this.__dnqpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DNQPoints', this.__dnqpoints, value);
                this.__dnqpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DNSPoints = values.DNSPoints ?? null;
        this.__dnspoints = values.DNSPoints ?? null;
        Object.defineProperty(this, 'DNSPoints', {
            get(): number | null {
                return this.__dnspoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DNSPoints', this.__dnspoints, value);
                this.__dnspoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DSQPoints = values.DSQPoints ?? null;
        this.__dsqpoints = values.DSQPoints ?? null;
        Object.defineProperty(this, 'DSQPoints', {
            get(): number | null {
                return this.__dsqpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DSQPoints', this.__dsqpoints, value);
                this.__dsqpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LAPPoints = values.LAPPoints ?? null;
        this.__lappoints = values.LAPPoints ?? null;
        Object.defineProperty(this, 'LAPPoints', {
            get(): number | null {
                return this.__lappoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('LAPPoints', this.__lappoints, value);
                this.__lappoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NonRankedPoints = values.NonRankedPoints ?? null;
        this.__nonrankedpoints = values.NonRankedPoints ?? null;
        Object.defineProperty(this, 'NonRankedPoints', {
            get(): number | null {
                return this.__nonrankedpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('NonRankedPoints', this.__nonrankedpoints, value);
                this.__nonrankedpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OrganizerID = values.OrganizerID ?? null;
        this.__organizerid = values.OrganizerID ?? null;
        Object.defineProperty(this, 'OrganizerID', {
            get(): number | null {
                return this.__organizerid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('OrganizerID', this.__organizerid, value);
                this.__organizerid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Season = values.Season ?? null;
        this.__season = values.Season ?? null;
        Object.defineProperty(this, 'Season', {
            get(): number | null {
                return this.__season;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Season', this.__season, value);
                this.__season = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShootingPoints = values.ShootingPoints ?? null;
        this.__shootingpoints = values.ShootingPoints ?? null;
        Object.defineProperty(this, 'ShootingPoints', {
            get(): number | null {
                return this.__shootingpoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ShootingPoints', this.__shootingpoints, value);
                this.__shootingpoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportID = values.SportID ?? null;
        this.__sportid = values.SportID ?? null;
        Object.defineProperty(this, 'SportID', {
            get(): number | null {
                return this.__sportid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SportID', this.__sportid, value);
                this.__sportid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CompletedPoints": ${JSON.stringify(this.CompletedPoints)}` +
            ',' +
            `"DNFPoints": ${JSON.stringify(this.DNFPoints)}` +
            ',' +
            `"DNQPoints": ${JSON.stringify(this.DNQPoints)}` +
            ',' +
            `"DNSPoints": ${JSON.stringify(this.DNSPoints)}` +
            ',' +
            `"DSQPoints": ${JSON.stringify(this.DSQPoints)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"LAPPoints": ${JSON.stringify(this.LAPPoints)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"NonRankedPoints": ${JSON.stringify(this.NonRankedPoints)}` +
            ',' +
            `"OrganizerID": ${JSON.stringify(this.OrganizerID)}` +
            ',' +
            `"Season": ${JSON.stringify(this.Season)}` +
            ',' +
            `"ShootingPoints": ${JSON.stringify(this.ShootingPoints)}` +
            ',' +
            `"SportID": ${JSON.stringify(this.SportID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Scoring from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CompletedPoints?: number | null;
        DNFPoints?: number | null;
        DNQPoints?: number | null;
        DNSPoints?: number | null;
        DSQPoints?: number | null;
        LAPPoints?: number | null;
        Name?: string | null;
        NonRankedPoints?: number | null;
        OrganizerID?: number | null;
        Season?: number | null;
        ShootingPoints?: number | null;
        SportID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Scoring {
        return new Scoring(values);
    }

    public static _FromServerJson(json: any): Scoring {
        return new Scoring({
            ID: json.ID,
            CompletedPoints: json.CompletedPoints ?? null,
            DNFPoints: json.DNFPoints ?? null,
            DNQPoints: json.DNQPoints ?? null,
            DNSPoints: json.DNSPoints ?? null,
            DSQPoints: json.DSQPoints ?? null,
            LAPPoints: json.LAPPoints ?? null,
            Name: json.Name ?? '',
            NonRankedPoints: json.NonRankedPoints ?? null,
            OrganizerID: json.OrganizerID ?? null,
            Season: json.Season ?? null,
            ShootingPoints: json.ShootingPoints ?? null,
            SportID: json.SportID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Scoring from another instance of Scoring.
     * @param other The Scoring you want to clone
     * @returns A cloned Scoring
     */
    public static FromOther(other: Scoring): Scoring {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Scoring._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Scoring.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'CompletedPoints'
            | 'DNFPoints'
            | 'DNQPoints'
            | 'DNSPoints'
            | 'DSQPoints'
            | 'LAPPoints'
            | 'Name'
            | 'NonRankedPoints'
            | 'OrganizerID'
            | 'Season'
            | 'ShootingPoints'
            | 'SportID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Scoring';
}
